import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import {
  useCreateOnboardingTemplateMutation,
  useOnboardBranchMutation,
  useOnboardingTemplatesQuery,
  useRemoveOnboardingTemplateMutation,
  type BrandDetail,
} from '../brandsApi';
import { messageOf } from '../lib';

/**
 * ADR-0023 §10 — franchise onboarding templates, and provisioning a new
 * franchisee branch from one. `brands.onboard`, its own permission — a
 * franchisor holds it without holding `brands.manage`, so it can stand up a
 * new branch under its own brand without the tenant-admin authority to
 * redraw brand membership afterward.
 *
 * Onboarding a branch does NOT publish a menu, even when the template names
 * a source brand version: the backend opens a DRAFT branch `MenuVersion`
 * that still goes through the ordinary menu-governance review (ADR-0011).
 * That review happens on the Menu Governance screen, not here — this screen
 * only says a draft was opened.
 */
export default function OnboardingPanel({ brand }: { brand: BrandDetail }) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canOnboard = can('brands.onboard');

  const [templateOpen, setTemplateOpen] = useState(false);
  const [onboardOpen, setOnboardOpen] = useState(false);
  const templatesQuery = useOnboardingTemplatesQuery(brand.id);
  const [removeTemplate] = useRemoveOnboardingTemplateMutation();
  const { confirm } = useConfirm();

  const removeOne = async (id: string, name: string) => {
    const ok = await confirm({
      title: t('brands.onboarding.removeConfirmTitle', { name }),
      confirmLabel: t('common.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await removeTemplate({ brandId: brand.id, templateId: id }).unwrap();
      toast.success(t('brands.onboarding.templateRemoved'));
    } catch (e) {
      toast.error(messageOf(e, t('brands.onboarding.templateRemoveFailed')));
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-start justify-between">
          <div>
            <CardTitle>{t('brands.onboarding.templatesTitle')}</CardTitle>
            <CardDescription>{t('brands.onboarding.templatesSubtitle')}</CardDescription>
          </div>
          {canOnboard && (
            <Button size="sm" variant="outline" onClick={() => setTemplateOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('brands.onboarding.newTemplate')}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {templatesQuery.isLoading ? (
            <LoadingState />
          ) : templatesQuery.error ? (
            <ErrorState
              message={messageOf(templatesQuery.error, t('brands.onboarding.templatesLoadFailed'))}
              onRetry={() => templatesQuery.refetch()}
            />
          ) : (templatesQuery.data ?? []).length === 0 ? (
            <EmptyState
              title={t('brands.onboarding.noTemplates')}
              hint={canOnboard ? t('brands.onboarding.noTemplatesHint') : undefined}
            />
          ) : (
            <ul className="divide-y">
              {(templatesQuery.data ?? []).map((template) => (
                <li key={template.id} className="flex items-center justify-between gap-3 py-3">
                  <div>
                    <p className="font-medium">
                      {template.name}
                      {template.isDefault && (
                        <Badge tone="brand" className="ms-2">
                          {t('brands.onboarding.default')}
                        </Badge>
                      )}
                    </p>
                    {template.sourceMenuVersionId && (
                      <p className="text-xs text-ink-muted">{t('brands.onboarding.seedsMenu')}</p>
                    )}
                  </div>
                  {canOnboard && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeOne(template.id, template.name)}
                      aria-label={t('common.delete')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {canOnboard && (
        <Card>
          <CardHeader>
            <CardTitle>{t('brands.onboarding.onboardTitle')}</CardTitle>
            <CardDescription>{t('brands.onboarding.onboardSubtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setOnboardOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('brands.onboarding.onboardBranch')}
            </Button>
          </CardContent>
        </Card>
      )}

      {templateOpen && (
        <TemplateFormDialog brandId={brand.id} onClose={() => setTemplateOpen(false)} />
      )}
      {onboardOpen && (
        <OnboardBranchDialog
          brand={brand}
          templates={templatesQuery.data ?? []}
          onClose={() => setOnboardOpen(false)}
        />
      )}
    </div>
  );
}

function TemplateFormDialog({ brandId, onClose }: { brandId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [create, { isLoading: saving }] = useCreateOnboardingTemplateMutation();

  const submit = async () => {
    if (!name.trim()) {
      setError(t('brands.onboarding.form.nameRequired'));
      return;
    }
    try {
      await create({ brandId, body: { name: name.trim(), isDefault } }).unwrap();
      toast.success(t('brands.onboarding.form.created'));
      onClose();
    } catch (e) {
      setError(messageOf(e, t('brands.onboarding.form.saveFailed')));
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('brands.onboarding.form.title')}
      description={t('brands.onboarding.form.description')}
      className="max-w-md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label={t('brands.onboarding.form.name')} required error={error ?? undefined}>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
          {t('brands.onboarding.form.isDefault')}
        </label>
      </div>
    </Dialog>
  );
}

function OnboardBranchDialog({
  brand,
  templates,
  onClose,
}: {
  brand: BrandDetail;
  templates: { id: string; name: string }[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [onboard, { isLoading: saving }] = useOnboardBranchMutation();

  const submit = async () => {
    setError(null);
    if (!name.trim() || !code.trim()) {
      setError(t('brands.onboarding.onboardForm.required'));
      return;
    }
    const ok = await confirm({
      title: t('brands.onboarding.onboardForm.confirmTitle', { name: name.trim() }),
      body: t('brands.onboarding.onboardForm.confirmBody', { brand: brand.name }),
      confirmLabel: t('brands.onboarding.onboardBranch'),
    });
    if (!ok) return;
    try {
      await onboard({
        brandId: brand.id,
        body: { name: name.trim(), code: code.trim(), templateId: templateId || undefined },
      }).unwrap();
      toast.success(t('brands.onboarding.onboardForm.created'));
      onClose();
    } catch (e) {
      setError(messageOf(e, t('brands.onboarding.onboardForm.saveFailed')));
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('brands.onboarding.onboardForm.title')}
      description={t('brands.onboarding.onboardForm.description')}
      className="max-w-md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? t('common.loading') : t('brands.onboarding.onboardBranch')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label={t('brands.onboarding.onboardForm.name')} required>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <Field label={t('brands.onboarding.onboardForm.code')} required hint={t('brands.onboarding.onboardForm.codeHint')}>
          <Input value={code} onChange={(e) => setCode(e.target.value)} />
        </Field>
        {templates.length > 0 && (
          <Field label={t('brands.onboarding.onboardForm.template')} hint={t('brands.onboarding.onboardForm.templateHint')}>
            <Select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
              <option value="">{t('brands.onboarding.onboardForm.noTemplate')}</option>
              {templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {error && <p className="text-sm text-[var(--destructive)]">{error}</p>}
      </div>
    </Dialog>
  );
}
